import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  CatalogueApiService,
  SearchCriteria,
} from '@angular-workspace/services';
import { AssetTypeMapperService } from '@angular-workspace/core';
import { HelperService } from '../../../shared/services/helper.service';

export interface BannerSearchResult {
  id: string;
  name: string;
  label: string;
  type: string[];
  displayType: string;
}

export interface BannerGroupedResults {
  datasets: BannerSearchResult[];
  models: BannerSearchResult[];
  usecases: BannerSearchResult[];
}

@Component({
  selector: 'app-banner',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './banner.html',
  styleUrl: './banner.scss',
})
export class Banner implements OnInit, OnDestroy {
  private readonly catalogueApiService = inject(CatalogueApiService);
  private readonly helperService = inject(HelperService);
  private readonly assetTypeMapper = inject(AssetTypeMapperService);
  private readonly router = inject(Router);

  readonly searchQuery = signal('');
  readonly searchResults = signal<BannerSearchResult[]>([]);
  readonly groupedResults = signal<BannerGroupedResults>({
    datasets: [],
    models: [],
    usecases: [],
  });
  readonly showResults = signal(false);
  readonly isLoading = signal(false);

  readonly statsDatasets = signal(0);
  readonly statsModels = signal(0);
  readonly statsUsecases = signal(0);
  readonly statsConcepts = signal(0);

  readonly visibleResultsCount = computed(
    () =>
      this.groupedResults().datasets.length + this.groupedResults().models.length
  );

  private readonly defaultAssetTypes = ['Dataset', 'Model', 'App'] as const;
  private readonly defaultFilterFields = [
    'id',
    'name',
    'type',
    'label',
  ] as const;
  private readonly minSearchLength = 3;
  private debouncedSearch: ((query: string) => void) | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.banner__search-container')) {
      this.showResults.set(false);
      this.searchQuery.set('');
    }
  }

  ngOnInit(): void {
    this.debouncedSearch = this.helperService.debounce(
      (query: string) => void this.performSearch(query),
      500
    );
    void this.loadStats();
  }

  ngOnDestroy(): void {
    this.debouncedSearch = null;
  }

  onSearchQueryChange(value: string): void {
    this.searchQuery.set(value);
    this.onSearchInput();
  }

  onSearchInput(): void {
    const trimmed = this.searchQuery().trim();
    if (trimmed.length < this.minSearchLength) {
      this.searchResults.set([]);
      this.groupedResults.set({ datasets: [], models: [], usecases: [] });
      this.showResults.set(false);
      return;
    }
    if (trimmed.length >= this.minSearchLength && this.debouncedSearch) {
      this.debouncedSearch(trimmed);
    }
  }

  onSearch(): void {
    const trimmed = this.searchQuery().trim();
    if (!trimmed) {
      this.showResults.set(false);
      return;
    }
    const results = this.searchResults();
    if (results.length > 0) {
      this.navigateToAsset(results[0]);
    } else {
      void this.performSearch(trimmed, true);
    }
  }

  onInputFocus(): void {
    if (
      this.searchResults().length > 0 &&
      this.searchQuery().trim().length >= this.minSearchLength
    ) {
      this.showResults.set(true);
    }
  }

  private async performSearch(
    query: string,
    navigateToSearchPage = false
  ): Promise<void> {
    try {
      this.isLoading.set(true);
      const response = await this.catalogueApiService.searchAllAssets(
        query,
        [...this.defaultAssetTypes],
        [...this.defaultFilterFields],
        1,
        1000
      );

      let raw: unknown[] = [];
      if (Array.isArray(response)) {
        raw = response;
      } else if (Array.isArray(response?.result)) {
        raw = response.result;
      } else if (Array.isArray(response?.data)) {
        raw = response.data;
      } else if (Array.isArray(response?.items)) {
        raw = response.items;
      }

      const mapped: BannerSearchResult[] = raw
        .map((item: unknown) => {
          const rec = item as Record<string, unknown>;
          const apiType = Array.isArray(rec['type'])
            ? String(rec['type'][0])
            : String(rec['type'] ?? '');
          const displayType = this.assetTypeMapper.mapApiFormatToAssetType(
            apiType
          );
          const id = String(rec['id'] ?? '');
          const name = String(rec['name'] ?? '');
          const label = String(rec['label'] ?? name ?? '');
          const typeArr = Array.isArray(rec['type'])
            ? rec['type'].map(String)
            : [apiType];
          return {
            id,
            name,
            label,
            type: typeArr,
            displayType,
          };
        })
        .filter((r) => r.id);

      this.searchResults.set(mapped);

      this.groupedResults.set({
        datasets: mapped.filter((r) => r.displayType === 'Dataset'),
        models: mapped.filter((r) => r.displayType === 'Model'),
        usecases: mapped.filter(
          (r) => r.displayType === 'Use Case' || r.displayType === 'App'
        ),
      });

      this.showResults.set(!navigateToSearchPage);
    } catch (error) {
      console.error('Banner search failed:', error);
      this.searchResults.set([]);
      this.groupedResults.set({ datasets: [], models: [], usecases: [] });
      this.showResults.set(false);
    } finally {
      this.isLoading.set(false);
    }
  }

  navigateToAsset(result: BannerSearchResult): void {
    this.showResults.set(false);
    this.searchQuery.set('');
    const apiType = result.type[0] ?? '';
    const label = result.label || result.name || '';

    if (apiType.includes('DataBank')) {
      void this.router.navigate(['/dataset', result.id]);
      return;
    }
    if (apiType.includes('AiModel')) {
      void this.router.navigate(['/model', result.id]);
      return;
    }
    if (apiType.includes('Apps') || apiType.includes('UseCase')) {
      void this.router.navigate(['/usecases'], {
        queryParams: { search: label },
      });
      return;
    }

    void this.router.navigate(['/datasets'], { queryParams: { search: label } });
  }

  viewAllResults(section: 'datasets' | 'models'): void {
    this.showResults.set(false);
    const q = this.searchQuery().trim();
    this.searchQuery.set('');
    const path = section === 'datasets' ? '/datasets' : '/models';
    void this.router.navigate([path], { queryParams: { search: q } });
  }

  closeResults(): void {
    this.showResults.set(false);
    this.searchQuery.set('');
  }

  private async loadStats(): Promise<void> {
    const countCriteria: SearchCriteria[] = [
      {
        searchType: 'term',
        field: 'type',
        values: ['adex:DataBank', 'adex:AiModel', 'adex:Apps'],
      },
    ];

    try {
      const [countResponse, conceptsResponse] = await Promise.all([
        this.catalogueApiService.getCount(countCriteria),
        this.catalogueApiService.getFilterList(
          [{ searchType: 'term', field: 'type', values: ['adex:DataBank'] }],
          ['concepts']
        ),
      ]);

      const countRow = countResponse?.result?.[0] as
        | Record<string, number>
        | undefined;
      if (countRow) {
        this.statsDatasets.set(
          this.getCountValueByType(countRow, 'adex:DataBank')
        );
        this.statsModels.set(this.getCountValueByType(countRow, 'adex:AiModel'));
        this.statsUsecases.set(this.getCountValueByType(countRow, 'adex:Apps'));
      }

      const apiData =
        conceptsResponse?.result ??
        conceptsResponse?.results ??
        conceptsResponse?.data ??
        conceptsResponse;
      const concepts = this.extractUniqueConceptValues(apiData);
      this.statsConcepts.set(concepts.length);
    } catch (error) {
      console.error('Failed to load banner stats:', error);
    }
  }

  /** Same shape handling as dataset-home `populateFilterSections` extractValues for `concepts`. */
  private extractUniqueConceptValues(data: unknown): string[] {
    const values: string[] = [];
    const getAtPath = (
      obj: Record<string, unknown>,
      path: string
    ): unknown => {
      const parts = path.split('.');
      let v: unknown = obj;
      for (const p of parts) {
        v = (v as Record<string, unknown>)?.[p];
      }
      return v;
    };

    if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
      for (const item of data as Record<string, unknown>[]) {
        const raw = getAtPath(item, 'concepts');
        if (Array.isArray(raw)) {
          for (const v of raw) {
            if (v !== null && v !== undefined && v !== '')
              values.push(String(v));
          }
        } else if (raw !== null && raw !== undefined && raw !== '') {
          values.push(String(raw));
        }
      }
    } else if (data && typeof data === 'object') {
      const d = data as Record<string, unknown>;
      const raw = getAtPath(d, 'concepts');
      if (Array.isArray(raw)) {
        for (const v of raw) {
          if (v !== null && v !== undefined && v !== '')
            values.push(String(v));
        }
      } else if (raw !== null && raw !== undefined && raw !== '') {
        values.push(String(raw));
      }
    }

    return [...new Set(values)];
  }

  /** API keys can arrive with different casing, e.g. `adex:apps` vs `adex:Apps`. */
  private getCountValueByType(
    countRow: Record<string, number>,
    typeKey: string
  ): number {
    const directValue = countRow[typeKey];
    if (typeof directValue === 'number') {
      return Number(directValue);
    }

    const normalizedTypeKey = typeKey.toLowerCase();
    const matchingKey = Object.keys(countRow).find(
      (key) => key.toLowerCase() === normalizedTypeKey
    );

    if (!matchingKey) {
      return 0;
    }

    return Number(countRow[matchingKey] ?? 0);
  }
}
